import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { Logo } from "../components/brand/Logo";
import { Loader2 } from "lucide-react";

export default function TradeLogin() {
  const { t } = useLang();
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = "Trade Login | Sambiva"; }, []);
  useEffect(() => { if (user && user.role === "partner") navigate("/portal"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const data = await login(email, password);
      if (data.role !== "partner") { setError("This login is for trade partners."); setLoading(false); return; }
      navigate("/portal");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
      setLoading(false);
    }
  };

  const field = "w-full px-4 py-3 rounded-xl border border-sambiva-charcoal/15 bg-white focus:outline-none focus:ring-2 focus:ring-sambiva-red/40 text-sm";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-sambiva-cream">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1719427131242-5a5dbe78740f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-sambiva-green/70" />
        <div className="relative h-full flex flex-col justify-end p-12 text-sambiva-cream">
          <p className="font-script text-3xl text-sambiva-yellow mb-2">Sambiva Trade</p>
          <h2 className="font-serif text-4xl mb-3">Brazilian food, built for business.</h2>
          <p className="text-sambiva-cream/80 max-w-md">Wholesale pricing, stock visibility and easy ordering — all in one place.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/"><Logo className="h-11 mb-8" /></Link>
          <h1 className="font-serif text-3xl text-sambiva-charcoal mb-2">{t("login.title")}</h1>
          <p className="text-sambiva-muted mb-8">{t("login.sub")}</p>

          {error && <div data-testid="login-error" className="mb-4 bg-sambiva-red/10 text-sambiva-red rounded-xl px-4 py-3 text-sm">{error}</div>}

          <form onSubmit={submit} className="space-y-4" data-testid="trade-login-form">
            <div>
              <label className="block text-sm font-medium text-sambiva-charcoal mb-1.5">{t("common.email")}</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={field} />
            </div>
            <div>
              <label className="block text-sm font-medium text-sambiva-charcoal mb-1.5">{t("common.password")}</label>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={field} />
            </div>
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-sm text-sambiva-red hover:underline">{t("login.forgot")}</Link>
            </div>
            <button data-testid="login-submit" disabled={loading} className="w-full bg-sambiva-red hover:bg-sambiva-deepred text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
              {loading && <Loader2 className="animate-spin" size={18} />} {t("login.signIn")}
            </button>
          </form>

          <p className="text-sm text-sambiva-muted mt-6 text-center">
            {t("login.notPartner")} <Link to="/partner" className="text-sambiva-green font-semibold hover:underline">{t("login.applyLink")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
